// components/ChaptersPanel.jsx
import React, { Component } from "react";
import ChapterActionsMenu from "./ChapterActionsMenu";
import SummaryBadge from "./SummaryBadge";
import SummaryPanelContainer from "./SummaryPanelContainer";
import SubmissionsBadge from "./SubmissionsBadge";
import ChapterSubmissionsModal from "./ChapterSubmissionsModal";
import MarkdownPreview from "../../../components/MarkdownPreview";
import { stripHtml } from "../../../../utils";

export default class ChaptersPanel extends Component {
  state = {
    showSummaryModal: false,
    selectedChapter: null,
    selectedSummaryId: null,
    submissionsModalChapter: null,
  };

  openChapter = (chapterId) => {
    const { navigate, project, readOnly } = this.props;
    if (readOnly) {
      alert("This project is archived. Restore it first to open chapters.");
      return;
    }
    const projectId = project?.id;
    if (!navigate || !projectId) return;
    navigate(`/project/${projectId}/chapter/${chapterId}`);
  };

  handleSummaryClick = async (chapter, summaryInfo, e) => {
    e.stopPropagation();
    
    // Always open the modal - generation happens inside via "Restart" button
    this.setState({
      showSummaryModal: true,
      selectedChapter: chapter,
      selectedSummaryId: summaryInfo.summaryId || null,
    });
  };

  closeSummaryModal = () => {
    this.setState({
      showSummaryModal: false,
      selectedChapter: null,
      selectedSummaryId: null,
    });
  };  

  openSubmissionsModal = (chapter, e) => {
    e?.stopPropagation?.();
    this.setState({ submissionsModalChapter: chapter });
  };

  closeSubmissionsModal = () => {
    this.setState({ submissionsModalChapter: null });
  };

  onDragStartChapter = (e, chapter) => {
    if (this.props.readOnly) return;
    e.dataTransfer.setData("text/plain", chapter.id);
    e.dataTransfer.effectAllowed = "move";
  };

  render() {
    const {
      project,
      loading,
      error,
      chapters = [],
      onArchiveChapter,
      onSetChapterStatus,
      readOnly,
      summariesMap = {},
      submissionsCountByChapter = {},
      onReloadSubmissions,
    } = this.props;

    const { showSummaryModal, selectedChapter, selectedSummaryId, submissionsModalChapter } = this.state;

    if (!project) return null;

    return (
      <>
        {/* Summary Modal */}
        <SummaryPanelContainer
          isOpen={showSummaryModal}
          onClose={this.closeSummaryModal}
          chapter={selectedChapter}
          summaryId={selectedSummaryId}
          projectId={project.id}
        />

        {/* Submissions inbox modal */}
        <ChapterSubmissionsModal
          isOpen={!!submissionsModalChapter}
          chapter={submissionsModalChapter}
          onClose={this.closeSubmissionsModal}
          onReviewed={onReloadSubmissions}
        />

        {/* Loading */}
        {loading && (
          <ul className="chapters-list">
            {[...Array(3)].map((_, i) => (
              <li key={i} className="chapter-row" style={{ opacity: 0.6 }}>
                <div className="chapter-index">{i + 1}</div>
                <div className="chapter-main">
                  <div className="chapter-title">Loading…</div>
                  <div className="chapter-meta">Please wait</div>
                </div>                
              </li>
            ))}
          </ul>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="projects-error">Could not load chapters: {error}</div>
        )}

        {/* Loaded */}
        {!loading && !error && (
          <ul
            className={`chapters-list${readOnly ? " read-only" : ""}`}
            aria-disabled={readOnly || undefined}
          >
            {chapters.length === 0 ? (
              <li className="chapter-row" style={{ cursor: "default" }}>
                <div className="chapter-index">–</div>
                <div className="chapter-main">
                  <div className="chapter-title">No chapters yet</div>
                  <div className="chapter-meta">
                    {readOnly ? "Restore this project to add chapters" : "Create one to get started"}
                  </div>
                </div>
              </li>
            ) : (
              chapters.map((c, idx) => {
                const chapterStatus = c.status || (c.archived_at ? "archived" : "active");
                // Check if there's at least one summary available in the entire map
                const hasAnySummary = Object.values(summariesMap).some(
                  s => s && (s.success || s.summaryId)
                );
                
                const summaryInfo = summariesMap[c.id] || {};
                
                // Disable badge if no summaries exist anywhere
                const isBadgeDisabled = !hasAnySummary && !summaryInfo.success && !summaryInfo.summaryId;
                
                return (
                <div className="chapter-row-wrap" key={c.id} style={{ position: "relative" }}>
                  <li
                    key={c.id}
                    className={`chapter-row${readOnly ? " is-locked" : ""}`}
                    draggable={!readOnly}
                    onDragStart={(e) => this.onDragStartChapter(e, c)}
                    onClick={() => this.openChapter(c.id)}
                    aria-disabled={readOnly || undefined}
                    title={readOnly ? "Restore to open" : `Open ${c.title}`}
                    style={{ cursor: readOnly ? "not-allowed" : "pointer" }}
                  >
                    <div className="chapter-index">{c.chapter_number}</div>
                    <div className="chapter-main">
                      <div className="chapter-title-row">
                        <span className="chapter-title-text">{c.title}</span>
                        <div className="chapter-title-badges">
                          <SummaryBadge
                            onClick={(e) => this.handleSummaryClick(c, summaryInfo, e)}
                            model={summaryInfo.model}
                            provider={summaryInfo.provider}
                            status={summaryInfo.status}
                            error={summaryInfo.error}
                            loading={false}
                            disabled={isBadgeDisabled}
                          />
                          <SubmissionsBadge
                            count={submissionsCountByChapter[c.id] || 0}
                            onClick={(e) => this.openSubmissionsModal(c, e)}
                          />
                          <span className="chapter-meta">
                            {new Date(c.updated_at || c.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      {c.content && (
                        <div className="chapter-excerpt">
                          <MarkdownPreview content={c.content} maxLength={120} />
                        </div>
                      )}
                    </div>
                  </li>    
                  <div className="row-kebab-anchor"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            paddingRight: 8,
                            // keep it above the li so clicks don't hit li:
                            zIndex: 2,
                            // optional: pull slightly outside li’s padding if needed
                            pointerEvents: "auto",
                          }}
                        >
                          <ChapterActionsMenu
                            placement="row"
                            items={chapterStatus === "archived"
                              ? [{
                                  key: "restore",
                                  label: "🔄 Restore to Active",
                                  onClick: () => onSetChapterStatus?.(c.id, "active"),
                                }]
                              : [{
                                  key: "draft",
                                  label: "📝 Move to Draft",
                                  onClick: () => onSetChapterStatus?.(c.id, "draft"),
                                }, {
                                  key: "archive",
                                  label: "🗄 Archive",
                                  onClick: () => onArchiveChapter?.(c.id),
                                }]}
                            chapter={c}
                            readOnly={readOnly}                                                   
                          />
                  </div>
                </div>
                );
              })
            )}
          </ul>
        )}
      </>
    );
  }
}
